<template>
  <div class="relative z-50 bg-[--header-bottom-bg-color]">
    <a href="#maincontent" class="skip-link">{{ $t("common.buttons.skip_to_main_content") }}</a>
    <nav
      ref="bottomHeader"
      class="relative z-[2] flex min-h-20 items-center bg-[color:var(--color-header-bottom-bg)] px-5 lg:gap-x-3 xl:gap-x-10"
      role="menu"
    >
      <!-- Logo Header -->
      <a href="/" :aria-label="$t('common.buttons.home')" role="menuitem">
        <VcImage
          :src="logo?.url"
          :aria-label="$context.storeName"
          width="150"
          height="24"
          class="mr-5 h-6 xl:mr-10"
          loading="eager"
          fallback-src="/static/thorlabs/logo.svg"
        />
      </a>

      <!-- Main Menu start -->
      <DesktopMenu :desktop-menu-items="desktopMenuItems" />

      <!-- Main Menu end -->
      <div class="ml-auto flex gap-x-4 py-3 xl:gap-x-8 xl:py-4">
        <SearchBar role="menuitem" />
        <div class="-mx-2 flex items-center">
          <template v-if="isAuthenticated">
            <div class="group relative">
              <button
                ref="showAccountButton"
                type="button"
                class="flex h-12 items-center px-3 group-hover:[&_span]:text-primary group-focus:[&_span]:text-primary group-hover:[&_svg]:text-primary group-focus:[&_svg]:text-primary"
                :aria-label="$t('common.links.account')"
                role="menuitem"
                @click="toggleAccount"
              >
                <VcIcon
                  name="user-filled"
                  class="size-6"
                  :class="accountOpen ? 'text-[color:var(--color-primary-a1)]' : 'text-[color:var(--color-neutral-a1)]'"
                />
                <span class="ml-1.5 font-medium">{{ $t(`shared.account.navigation.main_title`) }}</span>
              </button>

              <div
                v-show="accountOpen"
                ref="accountOpenElement"
                class="absolute right-0 top-[calc(100%+17px)] w-[260px] transition-all"
              >
                <!-- class="invisible absolute right-0 top-full w-80 opacity-0 transition-all group-hover:visible group-hover:top-[calc(100%+31px)] group-hover:opacity-100" -->
                <div
                  class="border border-b-[3px] border-[color:var(--color-neutral-a4)] border-b-[color:var(--color-primary-a1)] bg-white p-5"
                >
                  <div class="flex flex-col gap-2 text-base">
                    <div class="mt-2">
                      <span
                        v-if="!punchoutCookies || !punchoutCookies.signedInWithFallbackCredentials"
                        class="text-[--color-neutral-a2]"
                      >
                        {{ $t(`${greeting}`) }}{{ isJapanese ? " " : ", " }}{{ greetingName
                        }}{{ isJapanese ? "" : "!" }}
                      </span>
                      <span
                        v-if="punchoutCookies && punchoutCookies.signedInWithFallbackCredentials"
                        class="punchout-fallback text-[--color-neutral-a2]"
                        >{{ $t("common.messages.signed_in_via_punchout", [punchoutCookies.organizationName]) }}</span
                      >
                    </div>
                    <div class="my-4 h-px w-full bg-[color:var(--color-neutral-a4)]"></div>
                    <div class="flex flex-col gap-6">
                      <a
                        v-if="!punchoutCookies"
                        href="/account/account-info"
                        class="text-base hover:font-semibold"
                        @click="accountOpen = false"
                        >{{ $t("pages.cart.my_account_title") }}</a
                      >
                      <a href="/account/orders" class="text-base hover:font-semibold" @click="accountOpen = false">{{
                        $t("shared.account.navigation.links.my_orders")
                      }}</a>
                      <a
                        v-if="!punchoutCookies"
                        href="/account/quotes"
                        class="text-base hover:font-semibold"
                        @click="accountOpen = false"
                        >{{ $t("shared.account.navigation.links.my_quotes") }}</a
                      >
                    </div>
                    <div v-if="!punchoutCookies" class="my-4 h-px w-full bg-[color:var(--color-neutral-a4)]"></div>
                    <button
                      v-if="!punchoutCookies"
                      class="text-left text-base capitalize hover:font-semibold"
                      type="button"
                      @click="
                        () => {
                          signMeOut();
                          accountOpen = false;
                        }
                      "
                    >
                      {{ $t("shared.layout.header.link_logout") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="group relative">
              <button
                ref="showAccountButton"
                type="button"
                class="flex h-12 items-center px-3 group-hover:[&_span]:text-primary group-hover:[&_svg]:text-primary group-focus:[&_svg]:text-primary"
                :aria-label="$t('common.links.account')"
                role="menuitem"
                @click="toggleAccount"
              >
                <VcIcon
                  name="user-circle"
                  class="size-6 text-[color:var(--color-neutral-a1)]"
                  :class="{ 'text-primary': accountOpen }"
                />
                <span class="ml-1.5 font-medium capitalize">{{ $t(`pages.home.sign_in_form_title`) }}</span>
              </button>

              <div
                v-show="accountOpen"
                ref="accountOpenElement"
                class="absolute right-0 top-[calc(100%+17px)] w-[360px] transition-all"
              >
                <div
                  class="border border-b-[3px] border-[color:var(--color-neutral-a4)] border-b-[color:var(--color-primary-a1)] bg-white p-8"
                >
                  <SignInForm v-if="!punchoutCookies" @close-form="accountOpen = false" />
                  <div v-else class="punchout-active-session">Punchout active session</div>
                </div>
              </div>
            </div>
          </template>
          <template v-for="item in desktopMainMenuItems" :key="item.id">
            <div v-if="item.id === 'cart'" class="mini-cart">
              <BottomHeaderLink v-if="item.id === 'cart'" :link="item" :count="cartItemsCount" />
            </div>
          </template>
        </div>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside, syncRefs, useScrollLock } from "@vueuse/core";
import { sumBy } from "lodash";
import { defineProps, ref, shallowRef, onUnmounted, onMounted, computed } from "vue";
import { useNavigations } from "@/core/composables";
import {
  punchoutCookies,
  SESSION_STORAGE_REMOVE_OLD_CARD_FROM_PUNCHOUT_SESSION,
} from "@/core/utilities/punchout/punchoutCookies";
import { SignInForm, useSignMeOut } from "@/shared/account";
import { useLanguageCheck } from "@/shared/account/composables/useLanguageCheck";
import { useUser } from "@/shared/account/composables/useUser";
import { useFullCart } from "@/shared/cart";
import { SearchBar } from "@/shared/layout";
import BottomHeaderLink from "./bottom-header-link.vue";
import DesktopMenu from "./desktop-menu.vue";
import type { IDesktopMenuItem, ILogo } from "@/shared/thorlabs/blocks";

interface IProps {
  logo?: ILogo;
  desktopMenuItems?: IDesktopMenuItem[];
}

defineProps<IProps>();

const { isJapanese } = useLanguageCheck();
const { isAuthenticated, greeting, greetingName } = useUser();
const { cart, forceFetch, clearCart } = useFullCart();
const { desktopMainMenuItems } = useNavigations();
const { signMeOut } = useSignMeOut();

const bottomHeader = ref<HTMLElement | null>(null);
const catalogMenuElement = shallowRef<HTMLElement | null>(null);
const showAccountButton = shallowRef<HTMLElement | null>(null);
const showProductsMenuButton = shallowRef<HTMLElement | null>(null);
const catalogMenuVisible = ref(false);

const open = ref(false);
const accountOpen = ref(false);
const accountOpenElement = ref(null);

const cartItemsCount = computed(() => {
  return cart.value ? sumBy(cart.value.items, (x) => x.quantity) : 0;
});

onClickOutside(
  accountOpenElement,
  () => {
    accountOpen.value = false;
  },
  { ignore: [showAccountButton] },
);

const toggleAccount = () => {
  accountOpen.value = !accountOpen.value;
  open.value = false;
};

onClickOutside(
  catalogMenuElement,
  () => {
    catalogMenuVisible.value = false;
  },
  { ignore: [showProductsMenuButton] },
);

// This is the first place in the application where the cart is fetched from backend.
onMounted(async () => {
  // Check if we need to remove the old punhcout cart right after the authentication
  const removeOldPunchoutCart = sessionStorage.getItem(SESSION_STORAGE_REMOVE_OLD_CARD_FROM_PUNCHOUT_SESSION);
  if (removeOldPunchoutCart && punchoutCookies.value) {
    sessionStorage.removeItem(SESSION_STORAGE_REMOVE_OLD_CARD_FROM_PUNCHOUT_SESSION);
    // Directly call clearCart
    await clearCart(true);
  }
  await forceFetch();
  window.addEventListener("keydown", handleEscKey);

  // Refresh cart (and availability) when tab regains focus after being hidden
  document.addEventListener("visibilitychange", handleVisibilityChange);
});

onUnmounted(() => {
  window.removeEventListener("keydown", handleEscKey);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
});

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") {
    void forceFetch();
  }
};

function handleEscKey(event: KeyboardEvent) {
  if (event.key === "Escape") {
    accountOpen.value = false;
  }
}
syncRefs(catalogMenuVisible, useScrollLock(document.body));
</script>

<style scoped lang="scss">
:deep(.vc-button) {
  @apply min-h-[48px];
}
.punchout-active-session {
  display: flex;
  justify-content: center;
  align-items: center;
}
.punchout-fallback {
  display: block;
  margin-top: 2px;
}
</style>
